// app/page.js — Modern Landing Page for ShepherdSheep
'use client';

import Link from 'next/link';
import { useSession, signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { 
  FaUsers, 
  FaUserPlus, 
  FaEnvelope, 
  FaCalendarAlt,
  FaChartLine,
  FaHeart,
  FaArrowRight,
  FaGoogle,
  FaShieldAlt,
  FaCheckCircle,
  FaPrayingHands,
  FaMobileAlt,
  FaLock,
  FaBible
} from 'react-icons/fa';

export default function Home() {
  const { data: session, status } = useSession();
  const router = useRouter();
  
  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (status === 'authenticated') {
      router.push('/people');
    }
  }, [status, router]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Show landing page for non-logged-in users
  if (status === 'unauthenticated') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/5 backdrop-blur-md border-b border-white/10">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center shadow-lg">
                  <FaPrayingHands className="text-xl text-white" />
                </div>
                <span className="text-xl font-bold text-white">Shepherd Sheep</span>
              </div>
              <div className="flex items-center gap-4">
                <Link href="/login" className="text-white/80 hover:text-white transition-colors">
                  Sign In
                </Link>
                <Link 
                  href="/signup" 
                  className="bg-white text-blue-900 px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left">
                <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <FaBible className="text-blue-400" />
                  <span>Built for Ministry Leaders</span>
                </div>
                
                <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
                  Never Walk Alone in{' '}
                  <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    Ministry
                  </span>
                </h1>
                
                <p className="text-xl text-white/70 mb-8 max-w-xl">
                  Shepherd Sheep helps pastors and ministry leaders build meaningful 
                  discipleship relationships. Track contacts, log interactions, and 
                  never let anyone fall through the cracks.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => signIn('google', { callbackUrl: '/people' })}
                    className="bg-white text-slate-900 px-8 py-4 rounded-xl font-semibold flex items-center justify-center gap-3 hover:bg-blue-50 transition-all hover:shadow-lg hover:shadow-white/10"
                  >
                    <FaGoogle className="text-xl" />
                    Start Free with Google
                  </button>
                  
                  <Link 
                    href="/signup"
                    className="border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
                  >
                    Create Account
                    <FaArrowRight />
                  </Link>
                </div>
                
                <p className="text-white/50 text-sm mt-4">
                  ✓ Free for ministry use • ✓ No credit card required
                </p>
              </div>
              
              {/* Right Visual */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-3xl blur-3xl"></div>
                <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 shadow-2xl">
                  {/* Mock Dashboard Preview */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white/5 rounded-xl p-4 flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white font-bold">
                            {String.fromCharCode(64 + i)}
                          </div>
                          <div className="flex-1">
                            <div className="h-4 bg-white/20 rounded w-32 mb-2"></div>
                            <div className="h-3 bg-white/10 rounded w-24"></div>
                          </div>
                          <div className="text-white/50 text-sm">2 days ago</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6 p-4 bg-blue-500/20 rounded-xl border border-blue-400/30">
                      <div className="flex items-center gap-3">
                        <FaCalendarAlt className="text-blue-400" />
                        <span className="text-blue-200">3 follow-ups due today</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scripture Banner */}
        <section className="py-16 bg-white/5 border-y border-white/10">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <FaBible className="text-4xl text-blue-400 mx-auto mb-6" />
            <blockquote className="text-2xl md:text-3xl text-white font-light italic mb-4">
              &ldquo;Know well the condition of your flocks, and give attention to your herds.&rdquo;
            </blockquote>
            <cite className="text-white/60 text-lg">— Proverbs 27:23</cite>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Everything You Need to Shepherd Well
              </h2>
              <p className="text-white/60 text-lg max-w-2xl mx-auto">
                Simple, powerful tools designed specifically for pastoral ministry
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: FaUsers,
                  title: 'Contact Management',
                  description: 'Keep detailed profiles for everyone in your ministry. Track their journey and important details.',
                  color: 'from-blue-400 to-blue-600'
                },
                {
                  icon: FaCalendarAlt,
                  title: 'Follow-up Reminders',
                  description: 'Never miss a follow-up. Set custom frequencies and get reminded when it is time to reach out.',
                  color: 'from-purple-400 to-purple-600'
                },
                {
                  icon: FaEnvelope,
                  title: 'Interaction History',
                  description: 'Log every conversation, visit, and prayer. Build a complete picture of your pastoral care.',
                  color: 'from-pink-400 to-pink-600'
                },
                {
                  icon: FaMobileAlt,
                  title: 'SMS Integration',
                  description: 'Send text messages directly from the app. Keep communication personal and timely.',
                  color: 'from-green-400 to-green-600'
                },
                {
                  icon: FaLock,
                  title: 'Privacy First',
                  description: 'Your ministry data stays private and secure. Built with church privacy in mind.',
                  color: 'from-orange-400 to-orange-600'
                },
                {
                  icon: FaHeart,
                  title: 'Built with Love',
                  description: 'Created by pastors, for pastors. We understand the unique challenges of ministry.',
                  color: 'from-red-400 to-red-600'
                }
              ].map((feature, idx) => (
                <div key={idx} className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all hover:-translate-y-1">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    <feature.icon className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-white/60 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-4 bg-white/5">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Get Started in Minutes
              </h2>
              <p className="text-white/60 text-lg">
                Simple setup, powerful results
              </p>
            </div>
            
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: 'Create Your Account',
                  description: 'Sign up with your Google account. Quick, secure, and no passwords to remember.',
                  action: 'Get Started',
                  link: '/signup'
                },
                {
                  step: '02',
                  title: 'Get Admin Approval',
                  description: 'For security, an admin reviews new accounts. This keeps your ministry data safe.',
                  action: null,
                  link: null
                },
                {
                  step: '03',
                  title: 'Start Shepherding',
                  description: 'Add contacts, set reminders, and begin building meaningful discipleship relationships.',
                  action: 'Learn More',
                  link: '/login'
                }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6 items-start">
                  <div className="text-5xl font-bold text-white/20">{item.step}</div>
                  <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/60 mb-4">{item.description}</p>
                    {item.action && (
                      <Link 
                        href={item.link}
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        {item.action} <FaArrowRight />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial / Social Proof */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <FaHeart className="text-4xl text-red-400 mx-auto mb-8" />
            <blockquote className="text-2xl md:text-3xl text-white font-light mb-6">
              &ldquo;Shepherd Sheep has transformed how I care for my congregation. 
              I never forget to follow up anymore, and my people feel truly shepherded.&rdquo;
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white font-bold">
                P
              </div>
              <div className="text-left">
                <div className="text-white font-semibold">Pastor Example</div>
                <div className="text-white/50">Lead Pastor, Community Church</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Shepherd Better?
            </h2>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Join pastors and ministry leaders who are building deeper 
              relationships through intentional discipleship.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => signIn('google', { callbackUrl: '/people' })}
                className="bg-white text-slate-900 px-8 py-4 rounded-xl font-semibold flex items-center justify-center gap-3 hover:bg-blue-50 transition-all hover:shadow-lg"
              >
                <FaGoogle className="text-xl" />
                Start Free Today
              </button>
              
              <Link 
                href="/signup"
                className="border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
              >
                Create Account
                <FaArrowRight />
              </Link>
            </div>
            
            <p className="text-white/40 text-sm mt-6">
              Free for ministry use. No credit card required.
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                    <FaPrayingHands className="text-xl text-white" />
                  </div>
                  <span className="text-xl font-bold text-white">Shepherd Sheep</span>
                </div>
                <p className="text-white/50 text-sm">
                  Built for ministry leaders who care deeply about their flocks.
                </p>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">Product</h4>
                <ul className="space-y-2 text-white/50">
                  <li><Link href="/login" className="hover:text-white transition-colors">Sign In</Link></li>
                  <li><Link href="/signup" className="hover:text-white transition-colors">Create Account</Link></li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">Resources</h4>
                <ul className="space-y-2 text-white/50">
                  <li><span className="hover:text-white transition-colors cursor-pointer">Help Center</span></li>
                  <li><span className="hover:text-white transition-colors cursor-pointer">Best Practices</span></li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-white font-semibold mb-4">Legal</h4>
                <ul className="space-y-2 text-white/50">
                  <li><span className="hover:text-white transition-colors cursor-pointer">Privacy Policy</span></li>
                  <li><span className="hover:text-white transition-colors cursor-pointer">Terms of Service</span></li>
                </ul>
              </div>
            </div>
            
            <div className="pt-8 border-t border-white/10 text-center text-white/40 text-sm">
              <p>© 2026 Shepherd Sheep. Built for ministry. Never walk alone.</p>
            </div>
          </div>
        </footer>
      </div>
    );
  }
  
  // Dashboard for logged-in users (this shouldn't show due to redirect, but keeping as fallback)
  return null;
}
